import React, { useCallback, useEffect, useState } from "react";
import { api, AppSetting } from "../api/client";
import PageLoader from "../components/PageLoader";

interface JobInfo {
  name: string;
  description: string;
  interval: string;
  settingsKey: string;
  lastRun: string | null;
}

const JOBS: Omit<JobInfo, "lastRun">[] = [
  {
    name: "Напоминания о днях рождения",
    description:
      "Отправляет тренерам и администраторам пуш-уведомления о днях рождения игроков и тренеров — в сам день и за день до него. Запускается раз в час, срабатывает один раз в день после 09:00.",
    interval: "1 час",
    settingsKey: "last_birthday_reminder_date",
  },
  {
    name: "Победители месяца",
    description:
      "В последний день месяца автоматически назначает трёх лучших игроков каждой группы победителями — их награды ждут выбора приза (часовой пояс Asia/Vladivostok).",
    interval: "5 минут",
    settingsKey: "last_monthly_winners_run",
  },
  {
    name: "Очистка устаревших токенов устройств",
    description:
      "Удаляет токены устройств для пуш-уведомлений, которые не обновлялись 90 и более дней. Запускается при старте сервера и каждые 24 часа.",
    interval: "24 часа",
    settingsKey: "",
  },
];

const MEDICAL_CERT_LAST_RUN_KEY = "last_medical_cert_reminder_date";
const MEDICAL_CERT_DAYS_BEFORE_KEY = "medical_cert_reminder_days_before";
const MEDICAL_CERT_DEFAULT_DAYS_BEFORE = 1;

export default function ScheduledJobsPage() {
  const [jobs, setJobs] = useState<JobInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [medicalCertLastRun, setMedicalCertLastRun] = useState<string | null>(
    null
  );
  const [daysBefore, setDaysBefore] = useState<number>(
    MEDICAL_CERT_DEFAULT_DAYS_BEFORE
  );
  const [daysBeforeDraft, setDaysBeforeDraft] = useState<string>(
    String(MEDICAL_CERT_DEFAULT_DAYS_BEFORE)
  );
  const [savingDaysBefore, setSavingDaysBefore] = useState(false);
  const [daysBeforeError, setDaysBeforeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const settings = await api.listSettings();
      const settingsMap = new Map<string, AppSetting>();
      for (const s of settings) {
        settingsMap.set(s.key, s);
      }

      setJobs(
        JOBS.map((j) => ({
          ...j,
          lastRun: j.settingsKey
            ? settingsMap.get(j.settingsKey)?.value ?? null
            : null,
        }))
      );

      setMedicalCertLastRun(
        settingsMap.get(MEDICAL_CERT_LAST_RUN_KEY)?.value ?? null
      );

      const raw = settingsMap.get(MEDICAL_CERT_DAYS_BEFORE_KEY)?.value;
      const parsed = raw ? parseInt(raw, 10) : NaN;
      const initial = Number.isFinite(parsed) && parsed >= 0
        ? parsed
        : MEDICAL_CERT_DEFAULT_DAYS_BEFORE;
      setDaysBefore(initial);
      setDaysBeforeDraft(String(initial));
    } catch (err) {
      console.error("Failed to load job status", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveDaysBefore = async () => {
    const n = parseInt(daysBeforeDraft.trim(), 10);
    if (!Number.isFinite(n) || n < 0) {
      setDaysBeforeError("Введите неотрицательное целое число");
      return;
    }
    setDaysBeforeError(null);
    setSavingDaysBefore(true);
    try {
      await api.upsertSetting(MEDICAL_CERT_DAYS_BEFORE_KEY, String(n));
      setDaysBefore(n);
      setDaysBeforeDraft(String(n));
    } catch (err) {
      console.error("Failed to save days_before", err);
      setDaysBeforeError("Не удалось сохранить");
    } finally {
      setSavingDaysBefore(false);
    }
  };

  if (loading) return <PageLoader />;

  const draftChanged = daysBeforeDraft.trim() !== String(daysBefore);

  return (
    <div className="page">
      <h2>Фоновые задачи</h2>
      <p className="page-description">
        Фоновые задачи внутри серверного процесса. Статус только для просмотра —
        задачи настраиваются в коде.
      </p>

      <div className="jobs-grid">
        {jobs.map((job) => (
          <div key={job.name} className="job-card">
            <div className="job-header">
              <h3>{job.name}</h3>
            </div>
            <p className="job-description">{job.description}</p>
            <div className="job-meta">
              <div className="job-meta-item">
                <span className="job-meta-label">Интервал</span>
                <span className="job-meta-value">{job.interval}</span>
              </div>
              <div className="job-meta-item">
                <span className="job-meta-label">Последний запуск</span>
                <span className="job-meta-value">
                  {job.lastRun ?? "—"}
                </span>
              </div>
              {job.settingsKey && (
                <div className="job-meta-item">
                  <span className="job-meta-label">Ключ настройки</span>
                  <code className="job-meta-value">{job.settingsKey}</code>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="job-card">
          <div className="job-header">
            <h3>Напоминания о медсправках</h3>
          </div>
          <p className="job-description">
            Отправляет тренерам и администраторам пуш-уведомления об игроках,
            у которых скоро истекает или только что истекла медицинская справка.
            Запускается раз в час, срабатывает один раз в день после 09:00. Для
            каждого игрока два напоминания: за N&nbsp;дней до окончания срока
            (настраивается ниже) и в сам день окончания.
          </p>
          <div className="job-meta">
            <div className="job-meta-item">
              <span className="job-meta-label">Интервал</span>
              <span className="job-meta-value">1 час</span>
            </div>
            <div className="job-meta-item">
              <span className="job-meta-label">Последний запуск</span>
              <span className="job-meta-value">
                {medicalCertLastRun ?? "—"}
              </span>
            </div>
            <div className="job-meta-item">
              <span className="job-meta-label">Ключ настройки</span>
              <code className="job-meta-value">{MEDICAL_CERT_LAST_RUN_KEY}</code>
            </div>
          </div>

          <div className="job-setting">
            <label className="mav-input-label" htmlFor="medical-cert-days-before">
              За сколько дней до истечения слать пуш
            </label>
            <div className="job-setting-row">
              <input
                id="medical-cert-days-before"
                className="mav-input"
                type="number"
                min={0}
                step={1}
                value={daysBeforeDraft}
                onChange={(e) => setDaysBeforeDraft(e.target.value)}
                disabled={savingDaysBefore}
              />
              <span className="job-setting-unit">дн.</span>
              <button
                type="button"
                className="mav-btn-primary"
                onClick={saveDaysBefore}
                disabled={!draftChanged || savingDaysBefore}
              >
                {savingDaysBefore ? "Сохраняем…" : "Сохранить"}
              </button>
            </div>
            {daysBeforeError && (
              <span className="mav-hint is-error">{daysBeforeError}</span>
            )}
            <p className="job-setting-note">
              Ключ настройки: <code>{MEDICAL_CERT_DAYS_BEFORE_KEY}</code>. В сам
              день окончания срока справки пуш отправляется всегда.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
